<template>
    <div
        v-if="recommendedNext"
        class="recommended-next-cta"
        :class="{
            'recommended-next-cta--concept': isConceptTheme,
            'recommended-next-cta--next-up': isNextUpTheme,
            'recommended-next-cta--info': isInfoTheme,
        }"
        v-dark
    >
        <div class="recommended-next-cta__header" v-dark>
            <div
                class="recommended-next-cta__header-text"
                :class="{
                    'recommended-next-cta__header-text--concept': isConceptTheme,
                    'recommended-next-cta__header-text--next-up': isNextUpTheme,
                    'recommended-next-cta__header-text--info': isInfoTheme,
                }"
                v-dark
            >
                {{ recommendedNext.label }}
            </div>
        </div>
        <div
            class="recommended-next-cta__body"
            v-dark
        >
            <div
                class="recommended-next-cta__explanation"
                :class="{
                    'recommended-next-cta__explanation--concept': isConceptTheme,
                    'recommended-next-cta__explanation--next-up': isNextUpTheme,
                    'recommended-next-cta__explanation--info': isInfoTheme,
                    'recommended-next-cta__explanation--two-weakest-subjects': isTwoWeakestSubjectsRec,
                    'recommended-next-cta__explanation--level-up-after-first-quiz': isLevelUpAfterFirstQuizRec
                }"
                v-html="recommendedNext.message"
                v-dark
            />
            <PocketButton
                :is-dark-mode="isDarkMode"
                class="recommended-next-cta__button"
                :class="{
                    'recommended-next-cta__button--mobile': breakpoint === 'black-bear'
                }"
                @click="startRecommendedCTA"
            >
                {{ recommendedNext.buttonLabel }}
            </PocketButton>
        </div>
        <template v-if="isInfoTheme">
            <CommunityScoreIconDm
                v-if="isDarkMode"
                class="recommended-next-cta__icon"
                :class="{'recommended-next-cta__icon--mobile': breakpoint === 'black-bear'}"
            />
            <CommunityScoreIconLm
                v-else
                class="recommended-next-cta__icon"
                :class="{'recommended-next-cta__icon--mobile': breakpoint === 'black-bear'}"
            />
        </template>
        <Icon
            v-else
            :type="iconType"
            class="recommended-next-cta__icon"
            :class="{
                'recommended-next-cta__icon--mobile': breakpoint === 'black-bear',
                'recommended-next-cta__icon--concept': isConceptTheme,
                'recommended-next-cta__icon--next-up': isNextUpTheme,
                'recommended-next-cta__icon--info': isInfoTheme,
            }"
            v-dark
        />
        <div
            class="recommended-next-cta__border-eraser"
            :class="{
                'recommended-next-cta__border-eraser--mobile': breakpoint === 'black-bear',
                'recommended-next-cta__border-eraser--concept': isConceptTheme,
                'recommended-next-cta__border-eraser--next-up': isNextUpTheme,
                'recommended-next-cta__border-eraser--info': isInfoTheme,
            }"
            v-dark
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import UIKit, { utils } from '@pocketprep/ui-kit'
import { quizModule } from '@/store/quiz/module'
import { userModule } from '@/store/user/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { screenModule } from '@/store/screen/module'
import type { Study } from '@pocketprep/types'
import type { TActiveQuiz } from '@/store/quiz/state'
import { generateAnswerSeeds, localISODateString } from '@/utils'
import CommunityScoreIconDm from '@/components/RecommendedNext/CommunityScoreIconDm.vue'
import CommunityScoreIconLm from '@/components/RecommendedNext/CommunityScoreIconLm.vue'
import { analyticsModule } from '@/store/analytics/module'

const { Button: PocketButton, Icon } = UIKit

const router = useRouter()

const conceptQuizQuestions = ref<Promise<Study.Class.QuestionJSON[]> | null>(null)

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const examMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const recommendedNext = computed(() => {
    return quizModule.state.recommendedNext
})

const isConceptTheme = computed(() => {
    return recommendedNext.value?.style === 'concept-quiz'
})

const isNextUpTheme = computed(() => {
    return recommendedNext.value?.style === 'next-up'
})

const isInfoTheme = computed(() => {
    return recommendedNext.value?.style === 'info'
})

const isTwoWeakestSubjectsRec = computed(() => {
    return recommendedNext.value?.recommenderClass === 'TwoWeakestSubjects'
})

const isLevelUpAfterFirstQuizRec = computed(() => {
    return recommendedNext.value?.recommenderClass === 'LevelUpAfterFirstQuiz'
})

const iconType = computed(() => {
    return isConceptTheme.value
        ? 'recommendedFilter'
        : isNextUpTheme.value
            ? 'star'
            : 'heart'
})

const allQuizzes = computed(() => {
    return quizModule.getters.getQuizzes()
})

const retakeQuiz = computed(() => {
    return allQuizzes.value.find(q => q.objectId === recommendedNext.value?.config.retakeQuizId)
})

const retakeQuizQuestion = computed(() => {
    if (!retakeQuiz.value) {
        return null
    }
    const answerSerials = retakeQuiz.value.answers.map(ans => ans.questionSerial)
    const allQuestions = quizModule.getters.getAnsweredQuestions()

    return allQuestions.filter(q => answerSerials.includes(q.serial))
})

const recommendedConfig = computed(() => {
    return recommendedNext.value?.config
})

const startRecommendedCTA = () => {
    if (!recommendedConfig.value) {
        return
    }

    if (recommendedConfig.value.conceptQuizId) {
        startConceptQuiz()
    } else if (retakeQuiz.value) {
        retakeOldQuiz()
    } else if (recommendedConfig.value.quizMode !== undefined) {
        if (recommendedConfig.value.quizMode === 6) {
            router.push({
                name: 'level-up-intro',
                query: {
                    step: 'selectSubject',
                },
            })
        } else if (recommendedConfig.value.quizMode === 0) {
            startQuick10()
        } else if (recommendedConfig.value.quizMode === 10
            || recommendedConfig.value.quizMode === 2
        ) {
            startRecommendedCustomQuiz()
        } else if (recommendedConfig.value.quizMode === -1) {
            router.push({ name: 'qotd' })
        }
    }

    analyticsModule.actions.amplitudeTrack('Recommendation Clicked', {
        recommenderClass: recommendedNext.value?.recommenderClass,
        message: recommendedNext.value?.message,
        style: recommendedNext.value?.style,
        quizMode: recommendedNext.value?.config.quizMode,
        conceptQuizName: recommendedNext.value?.config.conceptQuizName,
        conceptQuizId: recommendedNext.value?.config.conceptQuizId,
        questionCount: recommendedNext.value?.config.questionCount,
    })
}

const startConceptQuiz = async () => {
    if (!examMetadata.value || !conceptQuizQuestions.value) {
        throw new Error('Missing data to start concept quiz')
    }

    const questions = await conceptQuizQuestions.value
    const answerSeeds = generateAnswerSeeds(questions)
    const conceptQuiz: TActiveQuiz = {
        questions,
        answers: [],
        examGuid: examMetadata.value.examGuid,
        mode: 'concept',
        startedAt: localISODateString(),
        currentQuestionIndex: 0,
        durationSeconds: 0,
        breakDurationSeconds: 0,
        timerType: 'exam',
        conceptQuizId: recommendedNext.value?.config.conceptQuizId,
        answerSeeds,
    }

    quizModule.actions.updateActiveQuiz(conceptQuiz)
    router.push({ name: 'quiz' })
}

const retakeOldQuiz = () => {
    if (!retakeQuiz.value || !retakeQuizQuestion.value) {
        return
    }

    const code = String(retakeQuiz.value.mode) as keyof typeof utils.studyModes
    const mode = utils.studyModes[code].shortName
    const quizQuestions = [ ...retakeQuizQuestion.value ]
    const answerSeeds = generateAnswerSeeds(quizQuestions)

    const quizToRetake: TActiveQuiz = {
        questions: quizQuestions,
        answers: [],
        examGuid: retakeQuiz.value.examGuid,
        mode,
        startedAt: localISODateString(),
        currentQuestionIndex: 0,
        durationSeconds: 0,
        breakDurationSeconds: 0,
        timerType: 'exam',
        answerSeeds,
    }

    quizModule.actions.updateActiveQuiz(quizToRetake)

    router.push({ name: 'quiz' })
}

const startQuick10 = async () => {
    await quizModule.actions.createActiveQuiz({ mode: 'quick10', questionCount: 10 })
    router.push({ name: 'quiz' })
}

const startRecommendedCustomQuiz = async () => {
    if (!recommendedConfig.value) {
        return
    }

    await quizModule.actions.createActiveQuiz({
        mode: recommendedConfig.value.quizMode === 10 ? 'custom' : 'missed',
        questionCount: recommendedConfig.value.questionCount,
        subjectIds: recommendedConfig.value.byoConfig?.subjectIds,
        include: {
            ...recommendedConfig.value.byoConfig?.include,
        },
    })

    router.push({ name: 'quiz' })
}

onMounted(() => {
    if (recommendedNext.value?.config.conceptQuizId) {
        conceptQuizQuestions.value = quizModule.actions.fetchConceptQuizQuestions({
            conceptQuizId: recommendedNext.value.config.conceptQuizId,
            questionCount: recommendedNext.value.config.questionCount || 10,
        })
    } else {
        conceptQuizQuestions.value = null
    }

    setTimeout(() => {
        analyticsModule.actions.amplitudeTrack('Recommendation Seen', {
            recommenderClass: recommendedNext.value?.recommenderClass,
            message: recommendedNext.value?.message,
            style: recommendedNext.value?.style,
            quizMode: recommendedNext.value?.config.quizMode,
            conceptQuizName: recommendedNext.value?.config.conceptQuizName,
            conceptQuizId: recommendedNext.value?.config.conceptQuizId,
            questionCount: recommendedNext.value?.config.questionCount,
        })
    }, 1000)
})

</script>

<style lang="scss" scoped>

.recommended-next-cta {
    padding: 20px 15px 10px 22px;
    width: 100%;
    max-width: 780px;
    border-radius: 10px 8px 6px 8px;
    box-sizing: border-box;
    color: $brand-black;
    position: relative;
    margin-top: 77px;
    right: 4px;

    // offset border
    &::after {
        content: "";
        position: absolute;
        top: 3.5px;
        left: 3.5px;
        right: -3.5px;
        bottom: -3.5px;
        border: 1.5px solid $caramel;
        border-radius: 8px;
        pointer-events: none;
    }

    &--concept {
        background-color: $barely-purple;

        &::after {
            border-color: $brand-purple;
        }
    }

    &--next-up {
        background-color: $manilla;

        &::after {
            border-color: $caramel;
        }
    }

    &--info {
        background-color: $barely-background;

        &::after {
            border-color: $pewter;
        }
    }


    &--dark#{&}--concept {
        background-color: rgba($orchid, 0.3);
        color: $barely-background;

        &::after {
            border-color: $orchid;
        }
    }

    &--dark#{&}--next-up {
        background-color: rgba($banana-bread, 0.2);
        color: $barely-background;

        &::after {
            border-color: $cheddar;
        }
    }

    &--dark#{&}--info {
        background-color: $pickled-bluewood;
        color: $pewter;

        &::after {
            border-color: $pewter;
        }
    }

    &__border-eraser {
        position: absolute;
        top: 11px;
        left: 3.5px;
        width: 1.5px;
        height: 33px;
        z-index: 1;

        &--mobile {
            top: 3.5px;
            left: auto;
            right: 23px;
            width: 42px;
            height: 1.5px;
        }

        &--next-up {
            background-color: $manilla;
        }

        &--concept {
            background-color: $barely-purple;
        }

        &--info {
            background-color: $barely-background;
        }

        &--dark#{&}--next-up {
            background-color: $brand-black;
            box-shadow: inset 0 0 0 9999px rgba($banana-bread, 0.2);
        }

        &--dark#{&}--concept {
            background-color: $brand-black;
            box-shadow: inset 0 0 0 9999px rgba($orchid, 0.3);
        }

        &--dark#{&}--info {
            background-color: $pickled-bluewood;
        }

    }

    &__icon {
        position: absolute;
        left: -10px;
        top: 15px;
        z-index: 2;

        &--mobile {
            left: auto;
            right: 28px;
            top: -8px;
        }

        &--next-up {
            color: $caramel;
        }

        &--concept {
            color: $brand-purple;
        }

        &--dark#{&}--next-up {
            color: $cheddar;
        }

        &--dark#{&}--concept {
            color: $orchid;
        }
    }

    &__button {
        min-width: fit-content;
        position: absolute;
        right: 15px;
        bottom: 12px;

        &--mobile {
            position: initial;
            width: 204px;
        }
    }

    @include breakpoint(brown-bear) {
        max-width: 708px;
        margin-top: 38px;
    }

    @include breakpoint(black-bear) {
        max-width: 334px;
        text-align: center;
        padding: 21px 15px 20px 20px;
        margin-top: 0;
        right: 0;
    }

    &__explanation {
        font-size: 16px;
        line-height: 22px;
        max-width: 610px;

        :deep(strong) {
            font-weight: 600;
        }

        &--info {
            max-width: 591px;
        }

        &--two-weakest-subjects,
        &--level-up-after-first-quiz {
            max-width: 580px;
        }

        &--dark {
            color: $barely-background;
        }

        @include breakpoint(brown-bear) {
            max-width: 539px;
        }

        @include breakpoint(black-bear) {
            font-size: 15px;
            line-height: 20px;
            margin-bottom: 16px;
        }
    }

    &__header-text {
        font-weight: 700;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 1px;
        color: $ash;
        margin-bottom: 8px;
        line-height: 15px;
        display: flex;
        align-items: center;

        @include breakpoint(black-bear) {
            display: block;
            margin-bottom: 5px;
        }

        &--dark#{&}--next-up {
            color: $buttermilk;
        }

        &--dark#{&}--concept {
            color: $barely-purple;
        }

        &--dark#{&}--info {
            color: $fog;
        }
    }

    &__body {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;

        @include breakpoint(black-bear) {
            display: block;
        }
    }
}
</style>
